import { PERMISSIONS } from '@ibe/constants';
import { Button, ErrorState, Field, FieldLabel, FieldSelect, LoadingState } from '@ibe/ui';
import { toIsoDate, todayUtc } from '@ibe/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { NotPermitted, PermissionGuard } from '../../../auth/permission-guard.js';
import { PropertySelect, usePropertyOptions } from '../../../components/form/property-select.js';
import { useApiList } from '../../../lib/api/hooks.js';
import { ratePlanOptionsPath, roomTypeOptionsPath } from '../api/endpoints.js';
import { useDailyRates, useTaxes } from '../api/queries.js';
import { ratePlanOption, roomTypeOption } from '../api/schemas.js';
import { RateTable } from '../components/rate-table.js';

const ISO_MONTH_LENGTH = 7;

function monthFromIso(date: string): string {
  return date.slice(0, ISO_MONTH_LENGTH);
}

function monthBounds(month: string): { from: string; to: string } {
  const [year, monthPart] = month.split('-');
  const yearNum = Number(year);
  const monthNum = Number(monthPart);
  const from = toIsoDate(new Date(Date.UTC(yearNum, monthNum - 1, 1)));
  const to = toIsoDate(new Date(Date.UTC(yearNum, monthNum, 0)));
  return { from, to };
}

function formatMonth(month: string): string {
  const [year, monthPart] = month.split('-');
  const at = new Date(Date.UTC(Number(year), Number(monthPart) - 1, 1));
  return at.toLocaleDateString('en-AU', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function shiftMonth(month: string, delta: number): string {
  const [year, monthPart] = month.split('-');
  const at = new Date(Date.UTC(Number(year), Number(monthPart) - 1 + delta, 1));
  return monthFromIso(toIsoDate(at));
}

export function DailyRatesPage(): React.JSX.Element {
  const [params, setParams] = useSearchParams();

  const month = params.get('month') ?? monthFromIso(todayUtc());
  const propertyId = params.get('propertyId') ?? '';
  const ratePlanId = params.get('ratePlanId') ?? '';
  const roomTypeId = params.get('roomTypeId') ?? '';

  const { from, to } = monthBounds(month);

  const ratePlans = useApiList({
    resource: 'ratePlans',
    path: ratePlanOptionsPath(propertyId),
    schema: ratePlanOption,
    params: { page: 1, pageSize: 100, scope: { propertyId } },
    enabled: propertyId !== '',
  });

  const roomTypes = useApiList({
    resource: 'roomTypes',
    path: roomTypeOptionsPath(propertyId),
    schema: roomTypeOption,
    params: { page: 1, pageSize: 100, scope: { propertyId } },
    enabled: propertyId !== '',
  });

  const properties = usePropertyOptions();

  const currency = ratePlans.data?.items.find((plan) => plan.id === ratePlanId)?.currency ?? '';

  const rates = useDailyRates(propertyId, ratePlanId, roomTypeId, from, to);
  const taxes = useTaxes(propertyId, currency);

  useEffect(() => {
    const next = new URLSearchParams(params);
    let changed = false;

    if (propertyId === '' && properties.isSuccess && properties.data.length > 0) {
      const first = properties.data[0];
      if (first !== undefined) {
        next.set('propertyId', first.value);
        changed = true;
      }
    }

    if (
      propertyId !== '' &&
      ratePlanId === '' &&
      ratePlans.isSuccess &&
      !ratePlans.isPlaceholderData &&
      ratePlans.data.items.length > 0
    ) {
      const first = ratePlans.data.items[0];
      if (first !== undefined) {
        next.set('ratePlanId', first.id);
        changed = true;
      }
    }

    if (
      propertyId !== '' &&
      roomTypeId === '' &&
      roomTypes.isSuccess &&
      !roomTypes.isPlaceholderData &&
      roomTypes.data.items.length > 0
    ) {
      const first = roomTypes.data.items[0];
      if (first !== undefined) {
        next.set('roomTypeId', first.id);
        changed = true;
      }
    }

    if (changed) {
      setParams(next, { replace: true });
    }
  }, [
    params,
    propertyId,
    ratePlanId,
    roomTypeId,
    properties.isSuccess,
    properties.data,
    ratePlans.isSuccess,
    ratePlans.isPlaceholderData,
    ratePlans.data,
    roomTypes.isSuccess,
    roomTypes.isPlaceholderData,
    roomTypes.data,
    setParams,
  ]);

  const ratePlanSelectOptions = useMemo(
    () =>
      ratePlans.data?.items.map((plan) => ({
        value: plan.id,
        label: `${plan.name ?? plan.id} (${plan.code ?? ''})`,
      })) ?? [],
    [ratePlans.data],
  );

  const roomTypeSelectOptions = useMemo(
    () =>
      roomTypes.data?.items.map((type) => ({
        value: type.id,
        label: `${type.name ?? type.id} (${type.code ?? ''})`,
      })) ?? [],
    [roomTypes.data],
  );

  const update = (changes: Readonly<Record<string, string | undefined>>): void => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === undefined || value === '') next.delete(key);
      else next.set(key, value);
    }
    setParams(next, { replace: true });
  };

  const setProperty = (value: string): void => {
    update({ propertyId: value, ratePlanId: undefined, roomTypeId: undefined });
  };

  const loading = ratePlans.isPending || roomTypes.isPending || rates.isPending || taxes.isPending;
  const error = ratePlans.error ?? roomTypes.error ?? rates.error ?? taxes.error ?? undefined;

  const canRead = loading || rates.data !== undefined;

  return (
    <PermissionGuard permission={PERMISSIONS.PRICING_READ} fallback={<NotPermitted />}>
      <section className="space-y-4">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-xl font-semibold">Daily rates</h1>
            <p className="text-sm text-muted-foreground">
              View daily rates for the selected room type and rate plan.
            </p>
          </div>
        </header>

        <div className="flex flex-wrap items-end gap-4">
          <div className="w-56">
            <PropertySelect value={propertyId} onChange={setProperty} required hideDescription />
          </div>

          <Field className="w-56">
            <FieldLabel>Rate plan</FieldLabel>
            <FieldSelect
              value={ratePlanId}
              onValueChange={(value) => {
                update({ ratePlanId: value });
              }}
              options={ratePlanSelectOptions}
              disabled={propertyId === '' || ratePlans.isPending}
              placeholder="Choose a rate plan…"
            />
          </Field>

          <Field className="w-56">
            <FieldLabel>Room type</FieldLabel>
            <FieldSelect
              value={roomTypeId}
              onValueChange={(value) => {
                update({ roomTypeId: value });
              }}
              options={roomTypeSelectOptions}
              disabled={propertyId === '' || roomTypes.isPending}
              placeholder="Choose a room type…"
            />
          </Field>

          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => {
                update({ month: shiftMonth(month, -1) });
              }}
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <span className="min-w-[10rem] px-2 text-center text-sm font-medium">
              {formatMonth(month)}
            </span>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => {
                update({ month: shiftMonth(month, 1) });
              }}
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                update({ month: monthFromIso(todayUtc()) });
              }}
            >
              Today
            </Button>
          </div>
        </div>

        {loading && !canRead ? (
          <LoadingState label="Loading daily rates" />
        ) : error !== undefined ? (
          <ErrorState
            description={error instanceof Error ? error.message : 'The rates could not be loaded.'}
            onRetry={() => {
              void ratePlans.refetch();
              void roomTypes.refetch();
              void rates.refetch();
              void taxes.refetch();
            }}
          />
        ) : rates.data === undefined || rates.data.length === 0 ? (
          <div className="rounded-md border p-8 text-center text-sm text-muted-foreground">
            No rates found for this month.
          </div>
        ) : (
          <RateTable rates={rates.data} currency={currency} taxes={taxes.data ?? []} />
        )}
      </section>
    </PermissionGuard>
  );
}
